import React, { useState } from "react";
import { Card, Box, Paper, Typography, Collapse, IconButton, Tooltip, Button, CircularProgress } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from "react-router-dom";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import NoteIcon from "@material-ui/icons/Note";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VideocamIcon from "@material-ui/icons/Videocam";
import GetAppIcon from "@material-ui/icons/GetApp";
import XLSX from "sheetjs-style";
import SubActivityService from "./Locality/Service/subActivityService";
import { formatDate } from "../../utils/dateFormat";
import StudentCards from "./UserProfileCard";
import { useEffect } from 'react';
import ActivityService from "./Locality/Service/activityService";
import SuperActivityService from "./Locality/Service/superActivityService";
import StudentService from "./Locality/Service/studentService";
const isPublishedActivityRecord = (record) =>
    String(record?.publicStatus || "").toLowerCase() === "publish";

const getPublishedRecords = (entry) => {
    const records = Array.isArray(entry?.activityRecordsId) ? entry.activityRecordsId : [];
    return records.filter(isPublishedActivityRecord);
};

const STATUS_KEYS = ["P", "WA", "WI", "M"];

const normalizeActivityKey = (key) => String(key ?? "").trim();

const getActivityKeyIndex = (key) => {
    const normalized = normalizeActivityKey(key);
    if (!normalized) return -1;
    return STATUS_KEYS.indexOf(normalized);
};

const isStatusVisibleForRecord = (record, statusCode) => {
    if (!record) return false;
    const currentIndex = getActivityKeyIndex(record.key);
    if (currentIndex < 0) return false;
    const statusIndex = STATUS_KEYS.indexOf(statusCode);
    if (statusIndex < 0) return false;
    return statusIndex <= currentIndex;
};

const getStatusHistoryEntry = (record, statusCode) => {
    if (!record || !isStatusVisibleForRecord(record, statusCode)) return null;
    const fromHistory = (record.statusHistory || []).find(
        (item) => normalizeActivityKey(item?.key) === statusCode,
    );
    if (fromHistory?.changedAt) return fromHistory;
    // Fallback when history is missing/empty: use the record date for the current key.
    if (normalizeActivityKey(record.key) === statusCode) {
        const changedAt = record.date || record.sfd || record.createdAt || null;
        return { key: statusCode, changedAt };
    }
    return fromHistory || null;
};

const hasDisplayableStatus = (record) => {
    if (!Array.isArray(record) || record.length === 0) return false;
    const published = getPublishedActivityRecord(record);
    if (!published) return false;
    if (getActivityKeyIndex(published.key) < 0) return false;
    return STATUS_KEYS.some((statusCode) => Boolean(getStatusHistoryEntry(published, statusCode)));
};

const getPublishedActivityRecord = (records) => {
    if (!Array.isArray(records) || records.length === 0) return null;
    const published = records.find(isPublishedActivityRecord);
    return published || null;
};

const getActivityImages = (record) => {
    if (!record) return [];
    if (Array.isArray(record.imageURL)) return record.imageURL.filter(Boolean);
    return record.imageURL ? [record.imageURL] : [];
};

const getActivityVideos = (record) => {
    if (!record) return [];
    if (Array.isArray(record.videoURL)) return record.videoURL.filter(Boolean);
    return record.videoURL ? [record.videoURL] : [];
};

const hasActivityMedia = (record) =>
    getActivityImages(record).length > 0 || getActivityVideos(record).length > 0;

const getActivityRemarks = (record) => (record?.remarks || "").trim();

const asCatalogUrlList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
};

/** Catalog explanation / images / videos on List of Activity or Exercise. */
const hasCatalogContent = (entity) => {
    if (!entity) return false;
    if (String(entity.notes || "").trim()) return true;
    return (
        asCatalogUrlList(entity.imageURL).length > 0 ||
        asCatalogUrlList(entity.videoURL).length > 0
    );
};

const getCatalogContentTooltip = (entity, scope) => {
    const notes = Boolean(String(entity?.notes || "").trim());
    const imageCount = asCatalogUrlList(entity?.imageURL).length;
    const videoCount = asCatalogUrlList(entity?.videoURL).length;
    const parts = [];
    if (notes) parts.push("explanation");
    if (imageCount > 0) {
        parts.push(`${imageCount} image${imageCount === 1 ? "" : "s"}`);
    }
    if (videoCount > 0) {
        parts.push(`${videoCount} video${videoCount === 1 ? "" : "s"}`);
    }
    const label = scope === "exercise" ? "exercise" : "activity";
    if (!parts.length) return `View ${label} details`;
    return `View ${label} ${parts.join(", ")}`;
};

const STATUS_TEXT = {
    P: "Presented",
    WA: "Works with Assistance",
    WI: "Works Independently",
    M: "Mastered",
};

/**
 * Excel rows for one Area of Work: one row per published activity/exercise of
 * the student, with its latest status (the record's current key) and that
 * status's date. Mirrors what the tracker table shows.
 */
const buildExportRows = (areaName, reportRows, selectedStudentId) => {
    const rows = [];
    (reportRows || []).forEach((activity) => {
        const seen = new Set();
        (activity.activityList || []).forEach((entry) => {
            const recordStudentId = entry?.studentId?._id || entry?.studentId;
            if (String(recordStudentId) !== String(selectedStudentId)) return;
            const published = getPublishedRecords(entry)[0];
            if (!published) return;

            const subActivity = published.subActivityId;
            const exerciseId = subActivity?._id || subActivity || null;
            const activityName =
                activity.activityName || published.activityId?.activityName || "-";
            const exerciseName = exerciseId ? subActivity?.subActivityName || "-" : "-";
            const dedupeKey = exerciseId ? String(exerciseId) : `__activity__:${activityName}`;
            if (seen.has(dedupeKey)) return;
            seen.add(dedupeKey);

            const statusCode = normalizeActivityKey(published.key);
            if (getActivityKeyIndex(statusCode) < 0) return;
            const statusEntry = getStatusHistoryEntry(published, statusCode);
            const changedAt = statusEntry?.changedAt || published.date || published.createdAt || null;

            rows.push({
                area: areaName || "-",
                activity: activityName,
                exercise: exerciseName,
                status: STATUS_TEXT[statusCode] || statusCode,
                date: changedAt ? formatDate(changedAt) || "-" : "-",
            });
        });
    });
    return rows;
};

const hasActivityData = (result = []) =>
    result.some(
        (activity) =>
            Array.isArray(activity?.activityList) &&
            activity.activityList.some((entry) => getPublishedRecords(entry).length > 0)
    );

const useStyles = makeStyles((theme) => ({
    pageWrapper: {
        padding: theme.spacing(2.5),
        background: "#fafafa",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1.5),
        },
    },
    selectorCard: {
        borderRadius: 22,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
        background: "#ffffff",
        border: "1px solid #d9d9d9",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
    },
    contentCard: {
        borderRadius: 24,
        padding: theme.spacing(3),
        background: "#ffffff",
        border: "1px solid #d9d9d9",
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(2),
            borderRadius: 18,
        },
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
        },
    },
    titleBlock: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.75),
    },
    title: {
        fontWeight: 800,
        fontSize: "1.85rem",
        color: "#1a1a1a",
        [theme.breakpoints.down("sm")]: {
            fontSize: "1.4rem",
        },
    },
    subtitle: {
        color: "#555",
        maxWidth: 620,
        lineHeight: 1.6,
        fontSize: "0.95rem",
    },
    actionButton: {
        borderRadius: 12,
        padding: theme.spacing(1.1, 2.2),
        textTransform: "none",
        fontWeight: 700,
        background: "linear-gradient(135deg, #0b5cc2 0%, #0b5cc2 100%)",
        color: "#ffffff",
        boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
        "&:hover": {
            background: "linear-gradient(135deg, #0b5cc2 0%, #1e40af 100%)",
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
    },
    tabsScroller: {
        overflowX: "auto",
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    tabsWrap: {
        display: "inline-flex",
        gap: theme.spacing(1.25),
        minWidth: "100%",
        [theme.breakpoints.down("sm")]: {
            gap: theme.spacing(1),
        },
    },
    tabButton: {
        border: "1px solid #cbd5e1",
        background: "#ffffff",
        color: "#333",
        fontWeight: 700,
        fontSize: "0.9rem",
        borderRadius: 14,
        padding: theme.spacing(1.15, 2),
        whiteSpace: "nowrap",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            borderColor: "#60a5fa",
            backgroundColor: "#e3eefb",
            color: "#0b5cc2",
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1, 1.5),
            fontSize: "0.82rem",
            borderRadius: 12,
        },
    },
    tabButtonActive: {
        background: "linear-gradient(135deg, #e3eefb 0%, #bfdbfe 100%)",
        color: "#0b5cc2",
        borderColor: "#60a5fa",
        boxShadow: "0 8px 18px rgba(59, 130, 246, 0.18)",
    },
    tableWrapper: {
        overflowX: "auto",
        borderRadius: 18,
        border: "1px solid #d9d9d9",
        background: "#ffffff",
    },
    groupContainer: {
        borderRadius: 18,
        border: "1px solid #d9d9d9",
        background: "#ffffff",
        overflow: "hidden",
    },
    groupHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.6, 2),
        background: "#fafafa",
        borderBottom: "1px solid #d9d9d9",
        cursor: "pointer",
        transition: "background 0.2s ease",
        "&:hover": {
            background: "#e3eefb",
        },
    },
    groupHeaderActive: {
        background: "#e3eefb",
    },
    groupHeaderLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        minWidth: 0,
    },
    groupChevron: {
        color: "#0b5cc2",
        fontSize: "1.4rem",
        flexShrink: 0,
    },
    groupTitle: {
        fontWeight: 800,
        fontSize: "1rem",
        color: "#1a1a1a",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    groupCount: {
        color: "#555",
        fontWeight: 600,
        fontSize: "0.85rem",
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    table: {
        width: "100%",
        minWidth: 760,
        borderCollapse: "separate",
        borderSpacing: 0,
    },
    headCell: {
        background: "#e3eefb",
        color: "#1e3a8a",
        fontWeight: 800,
        fontSize: "0.9rem",
        padding: theme.spacing(1.75, 1.25),
        textAlign: "center",
        borderBottom: "1px solid #e3eefb",
        position: "sticky",
        top: 0,
        zIndex: 1,
    },
    firstHeadCell: {
        textAlign: "left",
        minWidth: 220,
    },
    secondHeadCell: {
        textAlign: "left",
        minWidth: 220,
    },
    row: {
        "&:nth-child(even) td": {
            backgroundColor: "#fcfdff",
        },
        "&:hover td": {
            backgroundColor: "#f8fbff",
        },
    },
    activityCell: {
        padding: theme.spacing(1.4, 1.25),
        borderBottom: "1px solid #e5e7eb",
        fontWeight: 700,
        color: "#1a1a1a",
        backgroundColor: "#fafafa",
        minWidth: 220,
    },
    subActivityCell: {
        padding: theme.spacing(1.4, 1.25),
        borderBottom: "1px solid #e5e7eb",
        color: "#333",
        minWidth: 220,
    },
    exerciseNameCell: {
        display: "flex",
        alignItems: "center",
        gap: 4,
    },
    mediaInfoBtn: {
        marginLeft: 2,
        padding: 4,
        flexShrink: 0,
    },
    statusCell: {
        padding: theme.spacing(1.2, 1),
        borderBottom: "1px solid #e5e7eb",
        textAlign: "center",
        fontWeight: 700,
        minWidth: 120,
        fontSize: "0.88rem",
    },
    inactiveStatus: {
        color: "#8a8a8a",
    },
    clickableStatus: {
        cursor: "pointer",
    },
    statusCellActive: {
        boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.65)",
    },
    detailRow: {
        backgroundColor: "#fafafa",
        "& td": {
            borderBottom: "1px solid #d9d9d9",
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
        },
    },
    detailCell: {
        padding: theme.spacing(0, 1.5, 1.5),
    },
    detailStack: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.25),
    },
    noteExpandBox: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 1.5),
        borderRadius: 10,
        border: "1px solid #d9d9d9",
        backgroundColor: "#ffffff",
    },
    detailMeta: {
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "#8a8a8a",
        textTransform: "uppercase",
        marginBottom: 4,
    },
    observationText: {
        color: "#1a1a1a",
        fontSize: "0.9rem",
        lineHeight: 1.65,
        whiteSpace: "pre-wrap",
    },
    imageGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1.25),
        marginTop: theme.spacing(0.5),
    },
    imageCard: {
        position: "relative",
        borderRadius: 10,
        overflow: "hidden",
        border: "1px solid #d9d9d9",
        backgroundColor: "#ffffff",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.1)",
        },
    },
    detailImage: {
        width: 88,
        height: 88,
        objectFit: "cover",
        display: "block",
        cursor: "pointer",
    },
    detailVideo: {
        width: 88,
        height: 88,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#1a1a1a",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: "#1a1a1a",
        },
    },
    imageIndex: {
        position: "absolute",
        top: 6,
        left: 6,
        backgroundColor: "rgba(15, 23, 42, 0.72)",
        color: "#ffffff",
        fontSize: "0.68rem",
        fontWeight: 700,
        borderRadius: 6,
        padding: "2px 6px",
    },
    loadingBox: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#0b5cc2",
        fontWeight: 700,
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#555",
        fontWeight: 600,
    },
    helperText: {
        color: "#555",
        fontSize: "0.85rem",
        marginBottom: theme.spacing(1.5),
    },
}));

export default function ParentActivityTrackerTable() {
    const classes = useStyles();
    const history = useHistory();

    const [activityList, setActivityList] = useState([]);
    const [studentsList, setstudentsList] = useState([]);
    var [classId, setClassId] = useState(null);
    const [addSuperActivityList, setAddSuperActivityList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    var [isLoading, setIsLoading] = useState(false);
    const [addClassList, setAddClassList] = useState([]);
    var [studentId, setStudentId] = useState("");

    const [activeTab, setActiveTab] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedKeyDetail, setExpandedKeyDetail] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    const toggleKeyDetail = (rowKey, statusCode, hasStatusDate, publishedRecord) => {
        if (!hasStatusDate || !publishedRecord) return;
        const remarks = getActivityRemarks(publishedRecord);
        if (!remarks && !hasActivityMedia(publishedRecord)) return;

        setExpandedKeyDetail((prev) =>
            prev?.rowKey === rowKey && prev?.statusCode === statusCode
                ? null
                : { rowKey, statusCode }
        );
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId],
        }));
    };
    useEffect(() => {
        fetchStudentList();

        return () => {
            setStudentList([]);
            setAddSuperActivityList([]);
            setActivityList([]);
        }
    }, []);

    useEffect(() => {
        // Initial student is already loaded in fetchStudentList.
        // Keep this only as a safety net if studentId was cleared.
        if (!studentId && studentList.length > 0) {
            const firstStudent = studentList[0];
            const firstStudentId = firstStudent?._id || "";
            const firstClassId = firstStudent?.classId?._id || firstStudent?.classId || null;

            if (firstStudentId && firstClassId) {
                setStudentId(firstStudentId);
                setClassId(firstClassId);
                getSuperActivityList(firstStudentId, firstClassId);
            }
        }
    }, [studentList, studentId]);
    const colorMap = {
        "P": "#5C9882",
        "WI": "#E6B163",
        "WA": "#A75C9C",
        "M": '#147ad482'
    };
    const onSubmit = (data, studentsId, classsId) => {
        setIsLoading(true); // Start loading

        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const resolvedStudentId = studentsId ? studentsId : studentId;
        const resolvedClassId = classsId ? classsId : classId;

        const keys = {
            schooleId: userDetails?.schooleId?._id || userDetails?.schooleId || userDetails?.schoolId?._id || userDetails?.schoolId,
            isFuturePlanning: false,
            isInitLoad: true,
            superActivityId: data,
            studentId: resolvedStudentId,
            classId: resolvedClassId,
            startDate: "",
            endDate: "",
            status: true,
            publicStatus: "publish",
        };

        return ActivityService.findActivityReports(keys)
            .then((result) => {
                const safeResult = (result || []).filter((activity) =>
                    (activity.activityList || []).some((item) => {
                        const recordStudentId = item?.studentId?._id || item?.studentId;
                        return (
                            String(recordStudentId) === String(resolvedStudentId) &&
                            getPublishedRecords(item).length > 0
                        );
                    })
                );
                setActivityList(safeResult);
                setExpandedKeyDetail(null);

                const allStudents = [];
                safeResult.forEach(activity =>
                    (activity.activityList || []).forEach(rec => {
                        const student = rec.studentId;
                        if (student && !allStudents.find(s => String(s._id) === String(student._id))) {
                            allStudents.push(student);
                        }
                    })
                );
                setstudentsList(allStudents);
                return safeResult;
            })
            .catch((err) => {
                console.error(err);
                setActivityList([]);
                return [];
            })
            .finally(() => {
                setIsLoading(false); // End loading
            });
    };

    const areaHasPublishedForStudent = (reportRows, selectedStudentId) =>
        (reportRows || []).some((activity) =>
            (activity.activityList || []).some((item) => {
                const recordStudentId = item?.studentId?._id || item?.studentId;
                return (
                    String(recordStudentId) === String(selectedStudentId) &&
                    getPublishedRecords(item).length > 0
                );
            })
        );

    const getSuperActivityList = async (nextStudentId, nextClassId) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        try {
            const schoolId =
                userDetails?.schooleId?._id ||
                userDetails?.schooleId ||
                userDetails?.schoolId?._id ||
                userDetails?.schoolId;
            if (!schoolId || !nextClassId || !nextStudentId) {
                setAddSuperActivityList([]);
                setActivityList([]);
                setActiveTab('');
                return;
            }

            setIsLoading(true);
            const res = await SuperActivityService.getAllSuperActivityById(schoolId, {
                classId: nextClassId,
            });
            const areas = Array.isArray(res) ? res : [];

            const baseKeys = {
                schooleId: schoolId,
                isFuturePlanning: false,
                isInitLoad: true,
                studentId: nextStudentId,
                classId: nextClassId,
                startDate: "",
                endDate: "",
                status: true,
                publicStatus: "publish",
            };

            // Only keep Area of Work tabs that have published records for this student.
            const checked = await Promise.all(
                areas.map(async (area) => {
                    try {
                        const result = await ActivityService.findActivityReports({
                            ...baseKeys,
                            superActivityId: area._id,
                        });
                        return areaHasPublishedForStudent(result, nextStudentId) ? area : null;
                    } catch (err) {
                        console.error(err);
                        return null;
                    }
                })
            );

            const publishedAreas = checked.filter(Boolean);
            setAddSuperActivityList(publishedAreas);

            if (publishedAreas.length > 0) {
                setActiveTab(publishedAreas[0]._id);
                await onSubmit(publishedAreas[0]._id, nextStudentId, nextClassId);
            } else {
                setActiveTab('');
                setActivityList([]);
                setIsLoading(false);
            }
        } catch (err) {
            console.error(err);
            setAddSuperActivityList([]);
            setActivityList([]);
            setActiveTab('');
            setIsLoading(false);
        }
    };
    const fetchStudentList = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const body = {};

        if (userDetails.role === "PARENT") {
            body.userId = userDetails._id;
        } else if (userDetails.role === "SCHOOLE" || userDetails.role === "SUPERADMIN") {
            body.schooleId = userDetails.schooleId || userDetails.schoolId;
        } else if (userDetails.role === "TEACHER") {
            body.schooleId = userDetails.schooleId || userDetails.schoolId;
            if (userDetails.classId) body.classId = userDetails.classId;
            if (userDetails.sectionId) body.sectionId = userDetails.sectionId;
        }

        StudentService.findStudentList(body)
            .then((res) => {
                const mappedStudents = (res || []).map((student) => ({
                    ...student,
                    fullName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
                }));
                setStudentList(mappedStudents);

                if (mappedStudents.length > 0) {
                    setStudentId(mappedStudents[0]._id);
                    setClassId(mappedStudents[0].classId?._id || mappedStudents[0].classId);
                    //  onSubmit(mappedStudents[0]._id, mappedStudents[0].classId._id);
                    getSuperActivityList(mappedStudents[0]._id, mappedStudents[0].classId?._id || mappedStudents[0].classId);
                }

            })
            .catch((err) => console.error(err));
    };
    const handleTabClick = (superActivityId) => {

        setActiveTab(superActivityId);
        onSubmit(superActivityId, studentId, classId);
    };
    const handleStudentClick = (studentId, classId) => {
        setStudentId(studentId);
        setClassId(classId);
        getSuperActivityList(studentId, classId);
        // You can now fetch the student's assignments or do anything with this ID
    };
    /** Download every published activity of the selected child (all areas) as Excel. */
    const downloadActivitiesExcel = async () => {
        if (!studentId || !classId || addSuperActivityList.length === 0 || isExporting) return;
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        const schoolId =
            userDetails?.schooleId?._id ||
            userDetails?.schooleId ||
            userDetails?.schoolId?._id ||
            userDetails?.schoolId;

        setIsExporting(true);
        try {
            const rows = [];
            for (const area of addSuperActivityList) {
                const result = await ActivityService.findActivityReports({
                    schooleId: schoolId,
                    isFuturePlanning: false,
                    isInitLoad: true,
                    superActivityId: area._id,
                    studentId,
                    classId,
                    startDate: "",
                    endDate: "",
                    status: true,
                    publicStatus: "publish",
                });
                rows.push(...buildExportRows(area.superActivityName, result, studentId));
            }

            if (rows.length === 0) {
                alert("No published activities to download for this student.");
                return;
            }

            const student = studentList.find((s) => String(s._id) === String(studentId));
            const studentName = student?.fullName || "Student";
            const header = ["Area of Work", "Activity", "Exercise", "Latest Status", "Status Date"];
            const ws = XLSX.utils.aoa_to_sheet([
                header,
                ...rows.map((r) => [r.area, r.activity, r.exercise, r.status, r.date]),
            ]);
            ws["!cols"] = [{ wch: 24 }, { wch: 32 }, { wch: 32 }, { wch: 24 }, { wch: 14 }];
            header.forEach((_, col) => {
                const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })];
                if (cell) cell.s = { font: { bold: true }, fill: { fgColor: { rgb: "EFEFEF" } } };
            });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Activities");
            const safeName = studentName.replace(/[^a-z0-9]+/gi, "-").replace(/(^-|-$)/g, "") || "student";
            const today = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `${safeName}-activities-${today}.xlsx`);
        } catch (err) {
            console.error(err);
            alert("Could not download the activities. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    const getStudentReportList = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail"));

        if (studentId === '') {
            alert("Please Select Student");
            return;
        }
        const getstudentId = { "studentId": studentId, };

        SubActivityService.getStudentReport(getstudentId).then((res) => {
            // const blob = new Blob(res.pdfBuffer);
            const buffer = Buffer.from(res.pdfBuffer);
            const blob = new Blob([buffer]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style = "display: none";
            a.href = url;
            a.download = "studentreport.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            // const blob = new Blob([res.pdfBuffer]);
            // saveAs(blob, 'filename.pdf');
            // const file = new File(res.pdfBuffer, 'filename.txt', { type: 'text/plain' });
            // setStudentReportList(res);
        }).catch((err) => {
            alert(err.response.data.message)
        });
    }
    return (
        <Box className={classes.pageWrapper}>
            {studentList.length > 1 && (
                <Card className={classes.selectorCard}>
                    <StudentCards
                        studentList={studentList}
                        onStudentSelect={handleStudentClick}
                        selectedStudentId={studentId}
                    />
                </Card>
            )}

            <Paper className={classes.contentCard}>
                <Box className={classes.headerRow}>
                    <Box className={classes.titleBlock}>
                        <Typography className={classes.title}>Area Of Work</Typography>
                        <Typography className={classes.subtitle}>
                            Track the student&apos;s learning progress across each Montessori work area
                            with a cleaner layout that also works better on small screens.
                        </Typography>
                    </Box>
                    {studentId && addSuperActivityList.length > 0 && (
                        <Button
                            className={classes.actionButton}
                            onClick={downloadActivitiesExcel}
                            disabled={isExporting || isLoading}
                            startIcon={isExporting ? <CircularProgress size={16} style={{ color: "#ffffff" }} /> : <GetAppIcon />}
                        >
                            {isExporting ? "Preparing..." : "Download Excel"}
                        </Button>
                    )}
                </Box>

                {addSuperActivityList.length > 0 && (
                    <>
                        <Typography className={classes.helperText}>
                            Tap an area tab to switch categories. On small screens, swipe horizontally to view the full progress table.
                        </Typography>

                        <Box className={classes.tabsScroller}>
                            <Box className={classes.tabsWrap}>
                                {addSuperActivityList.map((area) => {
                                    const isActive = activeTab === area._id;
                                    return (
                                        <button
                                            key={area._id}
                                            type="button"
                                            onClick={() => handleTabClick(area._id)}
                                            className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ""}`}
                                        >
                                            {area.superActivityName}
                                        </button>
                                    );
                                })}
                            </Box>
                        </Box>
                    </>
                )}

                {!isLoading && studentId && addSuperActivityList.length === 0 && (
                    <Box className={classes.emptyState}>
                        No published Area of Work available for the selected student.
                    </Box>
                )}

                {studentId && !isLoading && addSuperActivityList.length > 0 && (() => {
                    const STATUS_LABELS = {
                        P: "Presented",
                        WA: "Works with Assistance",
                        WI: "Works Independently",
                        M: "Mastered",
                    };

                    const renderStatusCell = (statusCode, exercise, rowKey) => {
                        const publishedRecord = getPublishedActivityRecord(exercise.record);
                        const activityDetails = publishedRecord
                            ? getStatusHistoryEntry(publishedRecord, statusCode)
                            : null;

                        const hasStatusDate = Boolean(activityDetails?.changedAt);
                        const remarks = getActivityRemarks(publishedRecord);
                        const images = getActivityImages(publishedRecord);
                        const videos = getActivityVideos(publishedRecord);
                        const hasDetailContent = Boolean(remarks || hasActivityMedia(publishedRecord));
                        const isClickable = hasStatusDate && hasDetailContent && publishedRecord;
                        const isDetailOpen =
                            expandedKeyDetail?.rowKey === rowKey &&
                            expandedKeyDetail?.statusCode === statusCode;
                        const bgColor = colorMap[statusCode];

                        return (
                            <td
                                className={`${classes.statusCell} ${!activityDetails ? classes.inactiveStatus : ""} ${isClickable ? classes.clickableStatus : ""} ${isDetailOpen ? classes.statusCellActive : ""}`}
                                style={{
                                    backgroundColor: activityDetails ? bgColor : undefined,
                                    color: activityDetails ? "#ffffff" : undefined,
                                }}
                                onClick={() => {
                                    if (isClickable) {
                                        toggleKeyDetail(rowKey, statusCode, hasStatusDate, publishedRecord);
                                    }
                                }}
                                title={isClickable ? "View observation and media" : undefined}
                            >
                                {activityDetails && activityDetails.changedAt
                                    ? formatDate(activityDetails.changedAt)
                                    : "-"}
                            </td>
                        );
                    };

                    const renderKeyDetailRow = (exercise, rowKey, statusCode) => {
                        const publishedRecord = getPublishedActivityRecord(exercise.record);
                        if (!publishedRecord) return null;

                        const remarks = getActivityRemarks(publishedRecord);
                        const images = getActivityImages(publishedRecord);
                        const videos = getActivityVideos(publishedRecord);
                        const statusLabel = STATUS_LABELS[statusCode] || statusCode;
                        const statusEntry = getStatusHistoryEntry(publishedRecord, statusCode);
                        const statusDate = statusEntry?.changedAt
                            ? formatDate(statusEntry.changedAt)
                            : "";
                        const exerciseName = exercise?.name || "Activity";
                        const statusMeta = statusDate ? `${statusLabel} · ${statusDate}` : statusLabel;

                        return (
                            <tr key={`${rowKey}-${statusCode}-detail`} className={classes.detailRow}>
                                <td className={classes.detailCell} colSpan={5}>
                                    <Box className={classes.detailStack}>
                                        {remarks && (
                                            <Box className={classes.noteExpandBox}>
                                                <NoteIcon style={{ color: "#1a1a1a", marginTop: 2, flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography className={classes.detailMeta}>
                                                        Observation · {exerciseName} · {statusMeta}
                                                    </Typography>
                                                    <Typography className={classes.observationText}>
                                                        {remarks}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        {images.length > 0 && (
                                            <Box className={classes.noteExpandBox}>
                                                <PhotoLibraryIcon style={{ color: "#1a1a1a", marginTop: 2, flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography className={classes.detailMeta}>
                                                        Images · {exerciseName} · {statusMeta}
                                                    </Typography>
                                                    <Box className={classes.imageGrid}>
                                                        {images.map((url, idx) => (
                                                            <Box key={`${rowKey}-img-${idx}`} className={classes.imageCard}>
                                                                <span className={classes.imageIndex}>{idx + 1}</span>
                                                                <img
                                                                    src={url}
                                                                    alt={`${exerciseName} ${idx + 1}`}
                                                                    className={classes.detailImage}
                                                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}

                                        {videos.length > 0 && (
                                            <Box className={classes.noteExpandBox}>
                                                <VideocamIcon style={{ color: "#1a1a1a", marginTop: 2, flexShrink: 0 }} />
                                                <Box flex={1}>
                                                    <Typography className={classes.detailMeta}>
                                                        Videos · {exerciseName} · {statusMeta}
                                                    </Typography>
                                                    <Box className={classes.imageGrid}>
                                                        {videos.map((url, idx) => (
                                                            <Box key={`${rowKey}-vid-${idx}`} className={classes.imageCard}>
                                                                <span className={classes.imageIndex}>{idx + 1}</span>
                                                                <Box
                                                                    className={classes.detailVideo}
                                                                    onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === " ") {
                                                                            e.preventDefault();
                                                                            window.open(url, "_blank", "noopener,noreferrer");
                                                                        }
                                                                    }}
                                                                    aria-label={`Play video ${idx + 1}`}
                                                                >
                                                                    <PlayArrowIcon style={{ color: "#ffffff", fontSize: 36 }} />
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </td>
                            </tr>
                        );
                    };

                    const groups = (activityList || [])
                        .map((activity) => {
                        const matchingEntries = (activity.activityList || []).filter((item) => {
                            const recordStudentId = item?.studentId?._id || item?.studentId;
                            return (
                                String(recordStudentId) === String(studentId) &&
                                getPublishedRecords(item).length > 0
                            );
                        });

                        // Parent view: only published exercises for this student (not the full catalog).
                        const recordedExercises = matchingEntries
                            .map((entry) => {
                                const publishedRecords = getPublishedRecords(entry);
                                const subActivity = publishedRecords[0]?.subActivityId;
                                const activityRef = publishedRecords[0]?.activityId;
                                if (!subActivity) {
                                    return {
                                        id: null,
                                        name: activityRef?.activityName
                                            || activity.activityName
                                            || "-",
                                        isActivityLevel: true,
                                        catalogContent: activityRef || null,
                                        entry,
                                    };
                                }
                                return {
                                    id: subActivity?._id || subActivity,
                                    name: subActivity?.subActivityName || "-",
                                    isActivityLevel: false,
                                    catalogContent: subActivity || null,
                                    entry,
                                };
                            })
                            .filter(Boolean);

                        // Deduplicate by exercise id (keep first published match).
                        const seen = new Set();
                        const exerciseRows = recordedExercises.filter((exercise) => {
                            const key = exercise.id == null ? `__activity__:${exercise.name}` : String(exercise.id);
                            if (seen.has(key)) return false;
                            seen.add(key);
                            return true;
                        });

                        const exercises = exerciseRows.map((exercise) => {
                            const matchedEntry = exercise.entry || matchingEntries.find((entry) => {
                                const subActivity = getPublishedRecords(entry)[0]?.subActivityId;
                                const subActivityId = subActivity?._id || subActivity || null;
                                if (exercise.id == null) {
                                    return !subActivityId;
                                }
                                return String(subActivityId) === String(exercise.id);
                            }) || null;

                            const record = matchedEntry ? getPublishedRecords(matchedEntry) : undefined;
                            const published = record?.[0];
                            const catalogContent = exercise.isActivityLevel
                                ? (exercise.catalogContent
                                    || published?.activityId
                                    || {
                                        notes: activity.notes,
                                        imageURL: activity.imageURL,
                                        videoURL: activity.videoURL,
                                    })
                                : (exercise.catalogContent || published?.subActivityId || null);

                            return {
                                id: exercise.id,
                                name: exercise.name,
                                isActivityLevel: exercise.isActivityLevel,
                                catalogContent,
                                record,
                            };
                        }).filter((exercise) => hasDisplayableStatus(exercise.record) || getPublishedActivityRecord(exercise.record));

                        const performedCount = exercises.filter(
                            (exercise) => hasDisplayableStatus(exercise.record)
                        ).length;

                        const catalogActivityId =
                          activity?.id
                          || matchingEntries
                            .map((entry) => {
                              const published = getPublishedRecords(entry)[0];
                              const activityRef = published?.activityId;
                              return activityRef?._id || activityRef || null;
                            })
                            .find(Boolean)
                          || null;

                        const activityCatalogContent = {
                            notes: activity.notes || "",
                            imageURL: asCatalogUrlList(activity.imageURL),
                            videoURL: asCatalogUrlList(activity.videoURL),
                        };

                        return {
                            id: activity.id,
                            catalogActivityId,
                            activityName: activity.activityName,
                            activityCatalogContent,
                            hasActivityCatalogContent: hasCatalogContent(activityCatalogContent),
                            exercises,
                            performedCount,
                        };
                    })
                        .filter((group) => Array.isArray(group.exercises) && group.exercises.length > 0);

                    if (groups.length === 0) {
                        return (
                            <Box className={classes.emptyState}>
                                No published activity records available for the selected student.
                            </Box>
                        );
                    }

                    return (
                        <Box className={classes.groupContainer}>
                            {groups.map((group, groupIndex) => {
                                const groupKey = group.id ?? `group-${groupIndex}`;
                                const isOpen = !!expandedGroups[groupKey];
                                return (
                                    <Box key={groupKey}>
                                        <Box
                                            className={`${classes.groupHeader} ${isOpen ? classes.groupHeaderActive : ""}`}
                                            onClick={() => toggleGroup(groupKey)}
                                        >
                                            <Box className={classes.groupHeaderLeft}>
                                                {isOpen ? (
                                                    <KeyboardArrowDownIcon className={classes.groupChevron} />
                                                ) : (
                                                    <KeyboardArrowRightIcon className={classes.groupChevron} />
                                                )}
                                                <Typography className={classes.groupTitle}>
                                                    {group.activityName}
                                                </Typography>
                                                {group.catalogActivityId && group.hasActivityCatalogContent && (
                                                    <Tooltip
                                                        title={getCatalogContentTooltip(
                                                            group.activityCatalogContent,
                                                            "activity",
                                                        )}
                                                        arrow
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            className={classes.mediaInfoBtn}
                                                            aria-label="View activity explanation and media"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                history.push(`/app/activity-media/${group.catalogActivityId}`);
                                                            }}
                                                        >
                                                            <InfoOutlinedIcon style={{ fontSize: 18, color: "#1a1a1a" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Typography className={classes.groupCount}>
                                                {group.performedCount} {group.performedCount === 1 ? "exercise performed" : "exercises performed"}
                                            </Typography>
                                        </Box>

                                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                            <Box className={classes.tableWrapper} style={{ borderRadius: 0, border: "none" }}>
                                                <table className={classes.table}>
                                                    <thead>
                                                        <tr>
                                                            <th className={`${classes.headCell} ${classes.firstHeadCell}`}>Exercise</th>
                                                            <th className={classes.headCell}>Presented (P)</th>
                                                            <th className={classes.headCell}>Works with Assistance (WA)</th>
                                                            <th className={classes.headCell}>Works Independently (WI)</th>
                                                            <th className={classes.headCell}>Mastered (M)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {group.exercises.flatMap((exercise, index) => {
                                                            const rowKey = `${groupKey}-${exercise?.id || index}`;
                                                            const isDetailOpen = expandedKeyDetail?.rowKey === rowKey;
                                                            const openStatusCode = isDetailOpen
                                                                ? expandedKeyDetail?.statusCode
                                                                : null;

                                                            return [
                                                                <tr key={rowKey} className={classes.row}>
                                                                    <td className={classes.subActivityCell}>
                                                                        <Box className={classes.exerciseNameCell}>
                                                                            <span>{exercise?.name || "-"}</span>
                                                                            {(() => {
                                                                                const catalog = exercise?.catalogContent;
                                                                                const showExerciseMedia =
                                                                                    !exercise?.isActivityLevel &&
                                                                                    exercise?.id &&
                                                                                    hasCatalogContent(catalog);
                                                                                if (!showExerciseMedia) return null;
                                                                                return (
                                                                                    <Tooltip
                                                                                        title={getCatalogContentTooltip(
                                                                                            catalog,
                                                                                            "exercise",
                                                                                        )}
                                                                                        arrow
                                                                                    >
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            className={classes.mediaInfoBtn}
                                                                                            aria-label="View exercise explanation and media"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                history.push(
                                                                                                    `/app/activity-media/${exercise.id}?type=exercise`,
                                                                                                );
                                                                                            }}
                                                                                        >
                                                                                            <InfoOutlinedIcon
                                                                                                style={{ fontSize: 18, color: "#1a1a1a" }}
                                                                                            />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                );
                                                                            })()}
                                                                        </Box>
                                                                    </td>
                                                                    {renderStatusCell("P", exercise, rowKey)}
                                                                    {renderStatusCell("WA", exercise, rowKey)}
                                                                    {renderStatusCell("WI", exercise, rowKey)}
                                                                    {renderStatusCell("M", exercise, rowKey)}
                                                                </tr>,
                                                                openStatusCode
                                                                    ? renderKeyDetailRow(exercise, rowKey, openStatusCode)
                                                                    : null,
                                                            ].filter(Boolean);
                                                        })}
                                                    </tbody>
                                                </table>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })()}

                {isLoading && (
                    <Box className={classes.loadingBox}>Loading activity progress...</Box>
                )}
            </Paper>
        </Box>
    );
}
